import axiosInstance from "../axios/axiosInstance";
import { getActiveCompanyId } from "utils/companyContext";

const companyId = () => getActiveCompanyId();

const downloadBlob = async (url, filename) => {
    try {
        const response = await axiosInstance.get(url, { responseType: "blob" });
        const href = URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = href;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(href);
    } catch (err) {
        // When responseType is blob, error body is also a Blob — decode it to a readable string
        if (err?.response?.data instanceof Blob) {
            const text = await err.response.data.text();
            try {
                const json = JSON.parse(text);
                throw new Error(json?.message || json?.title || text);
            } catch (parseErr) {
                if (parseErr instanceof SyntaxError) throw new Error(text || "Download failed.");
                throw parseErr;
            }
        }
        throw err;
    }
};

export const getCompanyAuthDocs = async () => {
    return (await axiosInstance.get(`/companies/${companyId()}/documents/company_authorization_documents`)).data;
};

export const getCompanyTaxDocs = async () => {
    return (await axiosInstance.get(`/companies/${companyId()}/documents/company_tax_documents`)).data;
};

export const getContractorTaxDocs = async () => {
    return (await axiosInstance.get(`/companies/${companyId()}/documents/contractor_tax_documents`)).data;
};

export const getEmployeeTaxDocs = async () => {
    return (await axiosInstance.get(`/companies/${companyId()}/documents/employee_tax_documents`)).data;
};

export const downloadCompanyAuthDoc = (documentId) =>
    downloadBlob(
        `/companies/${companyId()}/documents/company_authorization_documents/${documentId}/download`,
        `auth_doc_${documentId}.pdf`
    );

export const downloadCompanyTaxDoc = (documentId) =>
    downloadBlob(
        `/companies/${companyId()}/documents/company_tax_documents/${documentId}/download`,
        `company_tax_${documentId}.pdf`
    );

export const downloadContractorTaxDoc = (documentId) =>
    downloadBlob(
        `/companies/${companyId()}/documents/contractor_tax_documents/${documentId}/download`,
        `contractor_tax_${documentId}.pdf`
    );

export const downloadEmployeeTaxDoc = (documentId) =>
    downloadBlob(
        `/companies/${companyId()}/documents/employee_tax_documents/${documentId}/download`,
        `employee_tax_${documentId}.pdf`
    );

export const downloadW2Pdf = (year) =>
    downloadBlob(
        `/companies/${companyId()}/tax-reports/w2-pdf?year=${year}`,
        `W2_${year}.pdf`
    );

export const download1099Pdf = (year) =>
    downloadBlob(
        `/companies/${companyId()}/tax-reports/1099-pdf?year=${year}`,
        `1099NEC_${year}.pdf`
    );

export const download941Pdf = (year, quarter) =>
    downloadBlob(
        `/companies/${companyId()}/tax-reports/941-pdf?year=${year}&quarter=${quarter}`,
        `Form941_${year}_Q${quarter}.pdf`
    );

// Publish individual W-2 PDFs per employee to DB + send email notifications
export const publishW2s = async (year) =>
    (await axiosInstance.post(`/companies/${companyId()}/tax-reports/publish-w2?year=${year}`)).data;

// Publish individual 1099-NEC PDFs per contractor to DB + send email notifications
export const publish1099s = async (year) =>
    (await axiosInstance.post(`/companies/${companyId()}/tax-reports/publish-1099?year=${year}`)).data;

// Publish corrected W-2c PDFs per employee to DB + send email notifications
export const publishW2cs = async (year) =>
    (await axiosInstance.post(`/companies/${companyId()}/tax-reports/publish-w2c?year=${year}`)).data;

// Download combined W-2c PDF (all employees)
export const downloadW2cPdf = (year) =>
    downloadBlob(
        `/companies/${companyId()}/tax-reports/w2c-pdf?year=${year}`,
        `W2c_${year}.pdf`
    );

// Publish 1099-MISC for manual recipients (rents, royalties, etc.)
export const publishForm1099Misc = async (year, recipients) =>
    (await axiosInstance.post(`/companies/${companyId()}/tax-reports/publish-1099misc?year=${year}`, recipients)).data;
